/**
 * Unified attachment preview overlay (JAISIU-4015 / JAISIU-4023).
 *
 * Single Lit template that powers BOTH the pre-send (composer) and
 * post-send (message) attachment preview experiences. Replaces the
 * previous split:
 *
 *   - `media-lightbox.ts`         — imperative image-only lightbox used
 *                                    by both the composer chips and the
 *                                    message-side `<img>` blocks.
 *   - `attachment-viewer.ts`      — Lit template, image + PDF + fallback,
 *                                    shipped by JAISIU-3767 but never
 *                                    wired into the chat view.
 *
 * ## Why one module, not two components
 *
 *   - The overlay chrome is identical: same overlay div, same role /
 *     aria-modal / click-outside / ESC handling, same header (icon,
 *     title, sub, download button when relevant, close button).
 *   - The body rendering is identical: an `<img>` for `image`, an
 *     `<iframe>` for `pdf`, and a download fallback for everything else.
 *   - The state machine is identical: open/close, ESC, click-outside,
 *     download, fallback close.
 *
 * Splitting them again by mode would just recreate the duplication
 * the ticket asks us to remove.
 *
 * ## How the modes differ
 *
 *   - **Pre-send (`mode: "pre-send"`)** — the chip sits in the
 *     composer before the file is sent. The download button is
 *     hidden (the file lives on the local disk; the operator
 *     doesn't need a "download my own upload" affordance). The
 *     filename in the header falls back to the local `File.name`
 *     when the attachment has not been stored yet.
 *   - **Post-send (`mode: "post-send"`)** — the chip lives in the
 *     chat transcript after the model has acknowledged the file.
 *     The download button is visible; the header shows the
 *     server-side filename and MIME.
 *
 * The body / overlay / keyboard / fallback logic is shared and
 * imported by both call sites. Mode is a discriminated union so
 * TypeScript narrows the props correctly at each call site.
 *
 * ## API surface (authoritative)
 *
 *   - `renderAttachmentPreviewOverlay(props)` — single Lit template
 *     entry point. Returns `nothing` when closed. Embed this in
 *     any Lit-based parent that owns the open/close state.
 *   - `openAttachmentPreviewOverlay(attachment, mode)` — imperative
 *     open helper for the call sites that cannot easily thread
 *     state through the parent (the composer chip and the
 *     message-side image block). Mounts the same Lit template
 *     into a managed singleton root in `document.body` and tears
 *     it down on close. Same chrome, same body, same keyboard —
 *     the rendering is unified; only the lifecycle is imperative.
 *   - `decideAttachmentPreviewAction(a, ev, mode)` — pure decision
 *     helper. Same modifier-key contract as before; mode only
 *     changes the body's download affordance, not the routing.
 *   - `downloadAttachment(a)` — shared anchor-click helper for the
 *     fallback (kept here so the call site only needs one import).
 *   - `mapComposerAttachmentToOverlay(att)` — pure mapping from a
 *     pre-upload `ChatAttachment` (with optional `dataUrl` /
 *     `sessionFileId` / `previewUrl`) to the narrow overlay shape,
 *     picking the best full-resolution URL. Exported so the
 *     composer pre-send flow and any post-send chip that wants the
 *     same precedence can share it.
 *   - `ATTACHMENT_PREVIEW_MAX_WIDTH_VW` / `..._HEIGHT_VH` — the
 *     shared body caps.
 *
 * ## Full-resolution preview features (JAISIU-4146)
 *
 *   - **Zoom in / out** for image bodies — the body is wrapped in a
 *     scroll-and-zoom container. Wheel-zoom and a +/-/Fit toolbar
 *     control the scale. At 1:1 and above the body becomes
 *     scrollable so the operator can pan around the image.
 *   - **Pan** — when zoomed past 1.0, mouse-drag pans the image
 *     inside the body. Cursor flips between `grab` and `grabbing`.
 *   - **PDF page scrolling** — the `<iframe>` keeps the browser's
 *     native scrollbars (the iframe is sized to the body so its
 *     scrollport is reachable from the keyboard and from a
 *     trackpad). The body's `tabindex="0"` makes the focus
 *     reachable so Page-Down / arrow keys land on the PDF.
 *   - **Prev / next navigation (←/→)** — when the caller passes a
 *     `batch` and `batchIndex` in {@link OpenAttachmentPreviewOptions},
 *     the overlay renders chevrons and listens for ArrowLeft /
 *     ArrowRight to walk the batch without re-opening. The
 *     imperative helper holds a ref to the active batch so callers
 *     can simply re-open at a different index.
 *   - **Decoupled reuse** — same props / same template / same
 *     imperative helper; pre-send and post-send call sites only
 *     need to choose `mode` and (optionally) `batch`.
 *
 * Migration:
 *   - `media-lightbox.ts` is now a thin shim that delegates to
 *     `openAttachmentPreviewOverlay({mode: "post-send"})`, so
 *     existing imports keep working.
 *   - `attachment-viewer.ts` is a thin re-export of the Lit
 *     template under its old name, so existing tests still pass.
 */
import { html, nothing, render, type TemplateResult } from "lit";
import { icons } from "../icons.ts";
import {
  attachmentDisplayName,
  attachmentIsPreviewable,
  attachmentKindForMime,
  type AttachmentKind,
  type ChatAttachment,
  type SessionFileKind,
} from "./attachment-types.ts";
import {
  attachmentIconColorClass,
  attachmentIconColorFamilyForAttachment,
  attachmentIconSvgForFamily,
} from "./attachment-icon-color.ts";
import { hasRenderableBlocks, renderDocumentBlocks } from "./attachment-document-blocks.ts";
import { applySeek, renderAudioProgressBar, updateAudioProgressBar } from "./audio-progress-bar.ts";
import {
  applySeek as applyVideoSeek,
  renderVideoProgressBar,
  updateVideoProgressBar,
} from "./video-progress-bar.ts";
import {
  requestAttachmentSeek,
  resetAttachmentPlaybackState,
  resolveAttachmentPlaybackTime,
} from "./media-playback-state.ts";

/** Maximum preview body width as a fraction of the viewport. */
export const ATTACHMENT_PREVIEW_MAX_WIDTH_VW = 90;
/** Maximum preview body height as a fraction of the viewport. */
export const ATTACHMENT_PREVIEW_MAX_HEIGHT_VH = 80;

/**
 * Which side of the message lifecycle the overlay is being used on.
 *
 * - `pre-send`  — composer tray, before the file has been sent. The
 *                 download button is hidden and the filename falls
 *                 back to the local `File.name`.
 * - `post-send` — message transcript, after the file has been sent.
 *                 The download button is visible.
 */
export type AttachmentPreviewMode = "pre-send" | "post-send";

/**
 * Props for the unified overlay. The `mode` field is the only one
 * that meaningfully differs between pre-send and post-send usage;
 * the caller is otherwise responsible for the same things the
 * previous split components were: an `open` boolean, the
 * attachment to render, and a close callback.
 */
export interface AttachmentPreviewOverlayProps {
  /** Whether the modal is currently shown. */
  open: boolean;
  /** The attachment to preview. Required when open; ignored otherwise. */
  attachment: ChatAttachment | null;
  /**
   * Which side of the chat lifecycle this overlay is being used on.
   * Controls only the download-affordance and the filename fallback
   * — the overlay chrome and body are identical.
   */
  mode: AttachmentPreviewMode;
  /** Called when the user closes the modal (Esc, click outside, X). */
  onClose: () => void;
  /**
   * Called when the user clicks the header "Download" button. If
   * omitted, the overlay falls back to navigating to the raw URL
   * (the browser handles it as a download when the server sends
   * the right `Content-Disposition`, or as a navigation otherwise).
   *
   * Ignored in `pre-send` mode: the download button is hidden
   * because the file is on the local disk already.
   */
  onDownload?: (a: ChatAttachment) => void;
  /**
   * Optional batch of attachments the user is paging through with
   * ← / →. When `batch.length > 1` the overlay renders prev/next
   * chevrons in the header and binds ArrowLeft / ArrowRight to
   * `onNavigate`. The active index is the position of `attachment`
   * in `batch`; the caller can either set `batchIndex` explicitly
   * (handy for the imperative helper) or let the overlay infer it
   * from `attachment.id`. Ignored for `pdf` bodies (pdfs have
   * their own page-by-page navigation inside the iframe).
   */
  batch?: ChatAttachment[] | null;
  /**
   * Index of `attachment` inside `batch`. Defaults to finding the
   * first element whose `id` matches `attachment.id`. Used by the
   * imperative helper so a re-open at a different index does not
   * have to re-find the matching id.
   */
  batchIndex?: number;
  /**
   * JAISIU-3533 — called when the operator picks a different sheet tab
   * of a multi-sheet workbook.
   *
   * The overlay template is presentational; it records the selection and
   * asks the host to re-render. The imperative helper wires this to its
   * own `paint`, so the table body is repainted with the new sheet.
   */
  onSelectSheet?: (attachmentId: string, sheetIndex: number) => void;
  /**
   * Called when the user navigates to a different member of
   * `batch` (via chevron click or ArrowLeft / ArrowRight). The
   * caller is expected to re-render with the new active
   * attachment — the Lit template is presentational only; it does
   * not mutate props internally. When the caller is the imperative
   * helper, the helper handles this for you.
   */
  onNavigate?: (next: ChatAttachment, nextIndex: number) => void;
  /**
   * JAISIU-4449 — load the *prepared preview* for an attachment that
   * does not carry one yet.
   *
   * The gateway's session-file prepare step produces the tabular
   * schema (columns + sample rows) and the extracted document text.
   * The overlay asks for it when the attachment is a spreadsheet or a
   * document and the model has no preview data yet; the resolved
   * attachment (with `tabular` / `text` filled in) is rendered.
   *
   * Optional — when omitted (or when the fetch yields nothing) the
   * overlay renders the honest "not analysed" message exactly as
   * before. The callback must never reject the render: a rejected
   * promise is treated as "no preview available".
   */
  loadPreview?: (a: ChatAttachment) => Promise<ChatAttachment | null>;
}

/**
 * Resolve the active index in `batch` for the given attachment.
 *
 * If `batchIndex` is a non-negative number, use it directly.
 * Otherwise find the first element whose `id` matches `attachment.id`.
 * If neither yields a hit, fall back to 0.
 */
function resolveBatchIndex(
  attachment: ChatAttachment,
  batch: ChatAttachment[] | null | undefined,
  batchIndex: number | undefined,
): number {
  if (!batch || batch.length === 0) return -1;
  if (typeof batchIndex === "number" && batchIndex >= 0 && batchIndex < batch.length) {
    return batchIndex;
  }
  const found = batch.findIndex((b) => b.id === attachment.id);
  return found >= 0 ? found : 0;
}

/**
 * Human-readable title for the overlay header, derived from the
 * attachment's render kind. The previous version hard-coded
 * "Image preview" / "PDF preview"; with the new kinds (audio,
 * video, spreadsheet, document) the title has to match the body
 * the user is actually looking at so the dialog label stays
 * accurate.
 *
 * Exported for unit tests — the title is part of the overlay's
 * public surface (screen readers announce the dialog by it) and
 * the test suite (vitest, node) drives it directly without
 * mounting a DOM.
 */
/**
 * JAISIU-4430 — per-kind glyph for the overlay header, so a video is not
 * labelled with the generic paperclip. Kept next to
 * {@link attachmentPreviewTitleFor} so title and icon cannot drift apart.
 */
export function attachmentPreviewIconFor(kind: AttachmentKind | undefined): string {
  switch (kind) {
    case "image":
      return "🖼";
    case "audio":
      return "♪";
    case "video":
      return "▶";
    case "pdf":
      return "📕";
    case "spreadsheet":
      return "▦";
    case "document":
      return "📄";
    default:
      return "📎";
  }
}

export function attachmentPreviewTitleFor(a: ChatAttachment): string {
  switch (a.kind) {
    case "image":
      return "Image preview";
    case "audio":
      return "Audio preview";
    case "video":
      return "Video preview";
    case "pdf":
      return "PDF preview";
    case "spreadsheet":
      return "Spreadsheet preview";
    case "document":
      return "Document preview";
    case "code":
      return "Code preview";
    case "file":
    default:
      return "Attachment preview";
  }
}

/**
 * Render the unified attachment preview overlay.
 *
 * Returns the overlay (or `nothing` when closed). The caller mounts
 * the result in the chat shell or composer shell — no portal, no
 * global registry, so the overlay is trivially testable and
 * embeddable in both contexts.
 */
export function renderAttachmentPreviewOverlay(
  props: AttachmentPreviewOverlayProps,
): TemplateResult | typeof nothing {
  if (!props.open || !props.attachment) {
    return nothing;
  }
  const a = props.attachment;

  if (!attachmentIsPreviewable(a)) {
    // Defensive: callers should have already filtered, but if a
    // non-previewable kind reaches the overlay we close immediately
    // and hand the user a download link rather than rendering
    // nothing.
    return renderOverlayFallback(props);
  }

  const displayName = attachmentDisplayName(a);
  const showDownload = props.mode === "post-send";
  // JAISIU-4435 — header chrome is per-kind, and the icon is a
  // type-specific Lucide glyph (`filePdf`, `fileSpreadsheet`, …) so a
  // PDF is not labelled with a generic document icon. The *title*
  // strings are identical to {@link attachmentPreviewTitleFor}, which
  // stays exported for the node tests.
  //
  // The header describes the body that is actually rendered, and the
  // body dispatches on `a.kind` (see the `switch` below). So the
  // header title / icon must use the *same* kind — resolving it
  // through the MIME instead (`overlayRenderKind`) would let a stale
  // MIME paint the header "Image preview" above a PDF body.
  const headerKind = (a.kind ?? "file") as AttachmentKind;
  const { title: bodyTitle, icon: bodyIcon } = headerChromeForKind(headerKind);
  // JAISIU-4450 — PDF / spreadsheet / document get the classic Office
  // colour treatment; every other kind keeps the monochrome glyph.
  //
  // The colour family is resolved from the MIME first, then the
  // filename, then the kind — independently of the header title. That
  // matters because the gateway reports a PDF as the coarse `document`
  // kind: without the MIME/filename override the PDF would be painted
  // blue ("Document") instead of red ("PDF").
  const iconColorFamily = attachmentIconColorFamilyForAttachment({
    kind: a.kind,
    mimeType: a.mimeType,
    mime: a.mime,
    filename: a.filename ?? a.name,
  });
  const iconColorClass = attachmentIconColorClass(iconColorFamily);
  const headerIcon =
    attachmentIconSvgForFamily(iconColorFamily) ?? bodyIcon;
  const idx = resolveBatchIndex(a, props.batch, props.batchIndex);
  const canNavigate = idx >= 0 && (props.batch?.length ?? 0) > 1;
  const hasPrev = canNavigate && idx > 0;
  const hasNext = canNavigate && idx < (props.batch?.length ?? 0) - 1;
  const navigateTo = (nextIndex: number) => {
    if (!props.batch || !props.onNavigate) return;
    const next = props.batch[nextIndex];
    if (!next) return;
    props.onNavigate(next, nextIndex);
  };
  const body = (() => {
    // Dispatch on `a.kind` — the same value the header chrome uses
    // (see the `headerKind` above), so the title always names the
    // body the operator is actually looking at. Both production call
    // sites (composer chips and transcript chips) map the gateway
    // family to the render kind before they get here.
    switch (a.kind) {
      case "image":
        return renderImageBody(a);
      case "audio":
        return renderAudioBody(a);
      case "video":
        return renderVideoBody(a);
      case "pdf":
        return renderPdfBody(a);
      case "spreadsheet":
        return renderSpreadsheetBody(a, props.onSelectSheet
          ? (index) => props.onSelectSheet?.(a.id, index)
          : undefined);
      case "document":
        return renderDocumentBody(a);
      default:
        // Reachable only for `code` / `file` / unknown kinds —
        // `attachmentIsPreviewable` already short-circuited to the
        // download-fallback for those, but the switch exhaustiveness
        // keeps the type system honest.
        return renderPdfBody(a);
    }
  })();

  return html`
    <div
      class="attachment-preview-overlay attachment-viewer-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="attachment-preview-title"
      data-testid="attachment-preview"
      data-attachment-id=${a.id}
      data-attachment-kind=${a.kind}
      data-preview-mode=${props.mode}
      data-batch-size=${canNavigate ? (props.batch?.length ?? 0) : 0}
      data-batch-index=${canNavigate ? idx : -1}
      @click=${(e: Event) => {
        if (e.target === e.currentTarget) {
          props.onClose();
        }
      }}
      @keydown=${(e: KeyboardEvent) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          props.onClose();
        } else if (
          e.key === "Home" &&
          canNavigate &&
          (props.batch?.length ?? 0) > 1
        ) {
          // Jump to the first member of the batch. Bound at the
          // overlay level so the keyboard user does not have to
          // focus the chevron first.
          e.preventDefault();
          e.stopPropagation();
          navigateTo(0);
        } else if (
          e.key === "End" &&
          canNavigate &&
          (props.batch?.length ?? 0) > 1
        ) {
          // Jump to the last member of the batch.
          e.preventDefault();
          e.stopPropagation();
          navigateTo((props.batch?.length ?? 1) - 1);
        } else if (e.key === " " || e.code === "Space") {
          // Space toggles play / pause on the audio or video
          // element. Bound on the overlay so the keyboard user
          // does not have to Tab into the media first. Skipped
          // for non-media kinds so Space does not "swallow" the
          // browser's default page-scroll behaviour in image
          // / pdf / office preview bodies.
          if (a.kind === "audio" || a.kind === "video") {
            const card = (e.currentTarget as HTMLElement | null)?.querySelector(
              '[data-testid="attachment-preview-audio"], [data-testid="attachment-preview-video"]',
            ) as HTMLMediaElement | null;
            if (card) {
              e.preventDefault();
              e.stopPropagation();
              if (card.paused) {
                void card.play().catch(() => {
                  // Autoplay / media-error rejections are silent
                  // — the user can click the native control to
                  // retry. We do not surface a toast here.
                });
              } else {
                card.pause();
              }
            }
          }
        }
        if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
        if (!canNavigate) return;
        // JAISIU-4427 — read the LIVE index off the DOM instead of
        // using the `idx` captured when this template was created.
        //
        // Lit reuses the same DOM node when the imperative helper
        // re-renders the overlay in place (`onNavigate` calls
        // `render(template, activeRoot)`), and the keydown listener
        // captured on that node still closes over the ORIGINAL `idx`.
        // Two ArrowRight presses in a row therefore both computed
        // `idx + 1` from index 0 and the operator never advanced past
        // the second item. `data-batch-index` is re-bound on every
        // render, so it is the one source that is never stale.
        const el = e.currentTarget as HTMLElement;
        const live = parseInt(el.getAttribute("data-batch-index") ?? "", 10);
        const current = Number.isFinite(live) ? live : idx;
        const delta = e.key === "ArrowLeft" ? -1 : 1;
        const target = current + delta;
        if (target < 0 || target >= (props.batch?.length ?? 0)) {
          // At either end: swallow the key so the page behind the
          // overlay does not scroll, but do not navigate.
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        navigateTo(target);
      }}
    >
      <div
        class="attachment-preview-card attachment-viewer-card"
        @click=${(e: Event) => e.stopPropagation()}
      >
        <header class="attachment-preview-header attachment-viewer-header">
          <div class="attachment-preview-title-wrap attachment-viewer-title-wrap">
            <span
              class="attachment-preview-icon attachment-viewer-icon ${iconColorClass}"
              data-attachment-icon-variant=${iconColorClass}
              aria-hidden="true"
            >
              ${headerIcon ?? ""}
            </span>
            <div>
              <h2
                id="attachment-preview-title"
                class="attachment-preview-title attachment-viewer-title"
              >
                ${bodyTitle}
              </h2>
              </h2>
              <p class="attachment-preview-sub attachment-viewer-sub">
                ${displayName}${
                  canNavigate
                    ? html` <span
                        class="attachment-preview-batch-position attachment-viewer-batch-position"
                        data-testid="attachment-preview-batch-position"
                        >(${idx + 1} of ${props.batch?.length ?? 0})</span
                      >`
                    : nothing
                }
              </p>
            </div>
          </div>
          <div class="attachment-preview-actions attachment-viewer-actions">
            ${
              canNavigate
                ? html`
                    <button
                      type="button"
                      class="btn icon attachment-preview-nav attachment-preview-nav--prev attachment-viewer-nav attachment-viewer-nav--prev"
                      title="Previous (←)"
                      aria-label="Previous attachment"
                      data-testid="attachment-preview-prev"
                      ?disabled=${!hasPrev}
                      @click=${() => navigateTo(idx - 1)}
                    >
                      ${icons.arrowLeft}
                    </button>
                    <button
                      type="button"
                      class="btn icon attachment-preview-nav attachment-preview-nav--next attachment-viewer-nav attachment-viewer-nav--next"
                      title="Next (→)"
                      aria-label="Next attachment"
                      data-testid="attachment-preview-next"
                      ?disabled=${!hasNext}
                      @click=${() => navigateTo(idx + 1)}
                    >
                      ${icons.arrowRight}
                    </button>
                  `
                : nothing
            }
            ${
              showDownload
                ? html`
                    <button
                      type="button"
                      class="btn icon"
                      title="Download ${displayName}"
                      aria-label="Download ${displayName}"
                      data-testid="attachment-preview-download"
                      @click=${() => {
                        if (props.onDownload) {
                          props.onDownload(a);
                        } else {
                          window.location.assign(a.url ?? "");
                        }
                      }}
                    >
                      ${icons.arrowDown}
                    </button>
                  `
                : nothing
            }
            <button
              type="button"
              class="btn icon attachment-preview-close attachment-viewer-close"
              title="Close preview (Esc)"
              aria-label="Close preview"
              data-testid="attachment-preview-close"
              @click=${props.onClose}
            >
              ${icons.x}
            </button>
          </div>
        </header>
        <div
          class="attachment-preview-body attachment-viewer-body"
          data-testid="attachment-preview-body"
        >
          ${body}
        </div>
      </div>
    </div>
  `;
}

/**
 * Minimum / maximum zoom factors for the image preview. The
 * minimum stays at `1` so the operator never sees a tiny image
 * that is smaller than the body — at `1.0` the image fills the
 * body's max-width × max-height cap (`object-fit: contain`), which
 * is the natural fit-to-viewport default. Above `1.0` the body
 * becomes scrollable so the operator can pan around the image.
 */
export const ATTACHMENT_PREVIEW_MIN_ZOOM = 1;
/** Maximum zoom factor. 4× is enough for pixel-peeping without
 *  exhausting memory on a 4K screenshot. */
export const ATTACHMENT_PREVIEW_MAX_ZOOM = 4;
/** Step taken by the + / − toolbar buttons. */
export const ATTACHMENT_PREVIEW_ZOOM_STEP = 0.25;

function clampZoom(z: number): number {
  if (!Number.isFinite(z) || z < ATTACHMENT_PREVIEW_MIN_ZOOM) {
    return ATTACHMENT_PREVIEW_MIN_ZOOM;
  }
  if (z > ATTACHMENT_PREVIEW_MAX_ZOOM) {
    return ATTACHMENT_PREVIEW_MAX_ZOOM;
  }
  return z;
}

/**
 * Zoomed image body.
 *
 * The wrapper is the scroll container; the inner `<img>` is
 * transformed with a CSS variable so the user can pan it with the
 * mouse while zoomed past 1×. Wheel events bump the zoom factor
 * (Ctrl-wheel) and the +/-/Fit toolbar buttons do the same. Click
 * on the wrapper resets pan offsets but not the zoom (a double
 * click toggles 1× ↔ 2× for power users).
 *
 * State is intentionally local to the wrapper: it lives on CSS
 * variables on the wrapper element, so a re-render that swaps the
 * `<img src>` (e.g. when the operator navigates to a different
 * batch member) does not lose the zoom level. The zoom level is
 * reset only when the wrapper re-mounts (Lit reuses the DOM node
 * when the wrapper element type does not change, so src-swaps
 * inside the same wrapper keep the zoom — the operator's mental
 * "I'm zoomed in" state survives nav).
 */
function renderImageBody(a: ChatAttachment): TemplateResult {
  // Inline data URLs (e.g. a composer's `dataUrl` placeholder) are
  // shown as-is; remote URLs go through the same path. We stamp
  // `data-attachment-id` on the wrapper so tests can find the
  // current image even when the user has navigated through a
  // batch and the host DOM has more than one `.attachment-preview-image`
  // across the test run.
  return html`
    <div
      class="attachment-preview-zoom attachment-viewer-zoom"
      data-testid="attachment-preview-zoom"
      data-attachment-id=${a.id}
      tabindex="0"
      style="--zoom:1;--pan-x:0px;--pan-y:0px;"
      @wheel=${(e: WheelEvent) => {
        const el = e.currentTarget as HTMLElement;
        // Ctrl+wheel is the standard "pinch" gesture on trackpads;
        // we honour it everywhere so mouse-wheel also works on
        // devices without a trackpad (one notch ≈ 0.25× step).
        const delta = -Math.sign(e.deltaY) * ATTACHMENT_PREVIEW_ZOOM_STEP;
        const current = parseFloat(el.style.getPropertyValue("--zoom")) || 1;
        const next = clampZoom(current + delta);
        el.style.setProperty("--zoom", next.toString());
        e.preventDefault();
        e.stopPropagation();
      }}
      @dblclick=${(e: MouseEvent) => {
        const el = e.currentTarget as HTMLElement;
        const current = parseFloat(el.style.getPropertyValue("--zoom")) || 1;
        // Toggle 1× ↔ 2× — the two most useful power-user stops.
        el.style.setProperty(
          "--zoom",
          (current > 1.01 ? ATTACHMENT_PREVIEW_MIN_ZOOM : 2).toString(),
        );
        el.style.setProperty("--pan-x", "0px");
        el.style.setProperty("--pan-y", "0px");
        e.preventDefault();
        e.stopPropagation();
      }}
      @mousedown=${(e: MouseEvent) => {
        const el = e.currentTarget as HTMLElement;
        const current = parseFloat(el.style.getPropertyValue("--zoom")) || 1;
        if (current <= 1.01) return; // pan only kicks in when zoomed
        const startX = e.clientX;
        const startY = e.clientY;
        const startPanX = parseFloat(el.style.getPropertyValue("--pan-x")) || 0;
        const startPanY = parseFloat(el.style.getPropertyValue("--pan-y")) || 0;
        el.classList.add("attachment-preview-zoom--panning");
        const onMove = (ev: MouseEvent) => {
          el.style.setProperty("--pan-x", `${startPanX + (ev.clientX - startX)}px`);
          el.style.setProperty("--pan-y", `${startPanY + (ev.clientY - startY)}px`);
        };
        const onUp = () => {
          el.classList.remove("attachment-preview-zoom--panning");
          window.removeEventListener("mousemove", onMove);
          window.removeEventListener("mouseup", onUp);
        };
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <img
        class="attachment-preview-image attachment-viewer-image"
        src=${a.url ?? ""}
        alt=${a.filename || "attached image"}
        loading="eager"
        decoding="sync"
        style="max-width:${ATTACHMENT_PREVIEW_MAX_WIDTH_VW}vw;max-height:${ATTACHMENT_PREVIEW_MAX_HEIGHT_VH}vh;"
        data-testid="attachment-preview-image"
        @error=${(e: Event) => {
          // JAISIU-4146 / inspection report §2: keep the in-app
          // preview open even when the server returns a broken
          // image; surface a non-blocking "could not decode"
          // placeholder that still respects the operator
          // constraint of in-app preview, not a forced download.
          const el = e.currentTarget as HTMLImageElement;
          el.classList.add("attachment-preview-image--error");
          el.alt = `${a.filename || "image"} failed to load`;
        }}
      />
      <div
        class="attachment-preview-zoom-toolbar attachment-viewer-zoom-toolbar"
        role="toolbar"
        aria-label="Image zoom"
        data-testid="attachment-preview-zoom-toolbar"
      >
        <button
          type="button"
          class="btn icon attachment-preview-zoom-out attachment-viewer-zoom-out"
          title="Zoom out"
          aria-label="Zoom out"
          data-testid="attachment-preview-zoom-out"
          @click=${(e: Event) => {
            const wrap = (e.currentTarget as HTMLElement).closest(
              ".attachment-preview-zoom",
            ) as HTMLElement | null;
            if (!wrap) return;
            const current = parseFloat(wrap.style.getPropertyValue("--zoom")) || 1;
            wrap.style.setProperty(
              "--zoom",
              clampZoom(current - ATTACHMENT_PREVIEW_ZOOM_STEP).toString(),
            );
            if ((parseFloat(wrap.style.getPropertyValue("--zoom")) || 1) <= 1.01) {
              wrap.style.setProperty("--pan-x", "0px");
              wrap.style.setProperty("--pan-y", "0px");
            }
            e.stopPropagation();
          }}
        >
          ${icons.zoomOut}
        </button>
        <button
          type="button"
          class="btn icon attachment-preview-zoom-fit attachment-viewer-zoom-fit"
          title="Fit to viewport (1×)"
          aria-label="Fit to viewport"
          data-testid="attachment-preview-zoom-fit"
          @click=${(e: Event) => {
            const wrap = (e.currentTarget as HTMLElement).closest(
              ".attachment-preview-zoom",
            ) as HTMLElement | null;
            if (!wrap) return;
            wrap.style.setProperty("--zoom", ATTACHMENT_PREVIEW_MIN_ZOOM.toString());
            wrap.style.setProperty("--pan-x", "0px");
            wrap.style.setProperty("--pan-y", "0px");
            e.stopPropagation();
          }}
        >
          ${icons.maximize}
        </button>
        <button
          type="button"
          class="btn icon attachment-preview-zoom-in attachment-viewer-zoom-in"
          title="Zoom in"
          aria-label="Zoom in"
          data-testid="attachment-preview-zoom-in"
          @click=${(e: Event) => {
            const wrap = (e.currentTarget as HTMLElement).closest(
              ".attachment-preview-zoom",
            ) as HTMLElement | null;
            if (!wrap) return;
            const current = parseFloat(wrap.style.getPropertyValue("--zoom")) || 1;
            wrap.style.setProperty(
              "--zoom",
              clampZoom(current + ATTACHMENT_PREVIEW_ZOOM_STEP).toString(),
            );
            e.stopPropagation();
          }}
        >
          ${icons.zoomIn}
        </button>
      </div>
    </div>
  `;
}

function renderPdfBody(a: ChatAttachment): TemplateResult {
  // The iframe gets a focused wrapper so the operator can Tab to
  // the body and use Page-Down / Arrow keys to scroll the PDF
  // (the iframe's own scrollbars handle mouse / trackpad scrolling;
  // the wrapper's `tabindex` makes the iframe a valid Tab stop so
  // keyboard-only operators can reach the scrollport without
  // having to click into the iframe first).
  return html`
    <div
      class="attachment-preview-pdf-wrap attachment-viewer-pdf-wrap"
      data-testid="attachment-preview-pdf-wrap"
      tabindex="0"
    >
      <iframe
        class="attachment-preview-iframe attachment-viewer-iframe"
        src=${a.url ?? ""}
        title=${a.filename || "attached PDF"}
        style="width:${ATTACHMENT_PREVIEW_MAX_WIDTH_VW}vw;height:${ATTACHMENT_PREVIEW_MAX_HEIGHT_VH}vh;"
        data-testid="attachment-preview-iframe"
      ></iframe>
    </div>
  `;
}

/**
 * JAISIU-4447 — refresh the audio progress bar from the media
 * element's own events.
 *
 * The native `<audio controls>` strip is gone, so the custom bar is
 * the only scrubber. To keep it honest we reconcile the media's
 * reported position against the PENDING seek: while the browser has
 * not landed the operator's seek yet, the bar keeps showing the
 * requested position instead of the stale one (a `timeupdate` that
 * lands mid-seek would otherwise snap the playhead back).
 */
function syncAudioProgress(media: HTMLAudioElement): void {
  if (!media) return;
  const attachmentId = media.dataset.attachmentId ?? "";
  const resolved = resolveAttachmentPlaybackTime(
    attachmentId,
    media.currentTime,
  );
  updateAudioProgressBar(media, resolved);
}

/**
 * JAISIU-4447 — refresh the video progress bar from the media
 * element's own events.
 *
 * Same contract as {@link syncAudioProgress}: the pending seek wins
 * over a stale media position until the browser lands it.
 */
function syncVideoProgress(media: HTMLVideoElement): void {
  if (!media) return;
  const attachmentId = media.dataset.attachmentId ?? "";
  const resolved = resolveAttachmentPlaybackTime(
    attachmentId,
    media.currentTime,
  );
  updateVideoProgressBar(media, resolved);
}

/**
 * Audio body — a native `<audio controls>` element wrapped in a
 * `tabindex="0"` container so the keyboard user can Tab to it and
 * Space to play/pause (the browser default). The wrapper is
 * width-capped to the standard preview max width and centred, so
 * a 4-minute MP3 looks the same in a 320-wide window as it does
 * on a 4K monitor.
 *
 * `preload="metadata"` keeps the network round-trip small: only
 * the file's headers + first frame are fetched, not the full
 * track. The full download starts when the user hits Play.
 *
 * `crossorigin` is intentionally NOT set — we are not reading
 * duration or waveform data, so CORS pre-flight against the
 * gateway is unnecessary. The user gets a free play/pause/seek
 * UI straight from the browser.
 *
 * ## JAISIU-4442 — seekable audio playback controls
 *
 * The native control chrome is the operator's only scrubber, and it
 * auto-hides on idle and gives no room to a readable elapsed / total
 * time label. On top of that it is unreachable for keyboard users
 * once the overlay chrome takes focus. So the overlay renders its OWN
 * always-visible control bar underneath the `<audio>`.
 *
 * The bar mirrors the video progress architecture (JAISIU-4437):
 *
 *   - the track is driven by the `<audio>` element's own
 *     `duration` / `currentTime`, read LIVE at gesture time;
 *   - clicking the track seeks to `ratio * duration`, dragging seeks
 *     continuously;
 *   - the rendered state is refreshed imperatively (see
 *     `updateAudioProgressBar`) from the media element's own events
 *     rather than by re-rendering the overlay — re-rendering would
 *     tear down and recreate the `<audio>` element on every tick,
 *     which restarts playback and discards the buffered range;
 *   - the native `controls` stay enabled, so the existing preview
 *     playback / volume behaviour is untouched.
 *
 * On top of the scrubber the bar exposes an explicit play / pause
 * button and a volume slider.
 */
function renderAudioBody(a: ChatAttachment): TemplateResult {
  return html`
    <div
      class="attachment-preview-audio-wrap attachment-viewer-audio-wrap"
      data-testid="attachment-preview-audio-wrap"
      tabindex="0"
    >
      <audio
        class="attachment-preview-audio attachment-viewer-audio"
        preload="metadata"
        src=${a.url ?? ""}
        title=${a.filename || "attached audio"}
        data-testid="attachment-preview-audio"
        data-attachment-id=${a.id}
        @loadedmetadata=${(e: Event) => {
          syncAudioProgress(e.currentTarget as HTMLAudioElement);
        }}
        @durationchange=${(e: Event) => {
          syncAudioProgress(e.currentTarget as HTMLAudioElement);
        }}
        @timeupdate=${(e: Event) => {
          syncAudioProgress(e.currentTarget as HTMLAudioElement);
        }}
        @seeked=${(e: Event) => {
          syncAudioProgress(e.currentTarget as HTMLAudioElement);
        }}
        @play=${(e: Event) => {
          syncAudioProgress(e.currentTarget as HTMLAudioElement);
        }}
        @pause=${(e: Event) => {
          syncAudioProgress(e.currentTarget as HTMLAudioElement);
        }}
        @ended=${(e: Event) => {
          syncAudioProgress(e.currentTarget as HTMLAudioElement);
        }}
        @volumechange=${(e: Event) => {
          syncAudioProgress(e.currentTarget as HTMLAudioElement);
        }}
      ></audio>
      ${renderAudioProgressBar(
        (track: HTMLElement) => {
          // Resolve the media element relative to the bar that owns
          // the gesture. The bar and the `<audio>` share the wrapper,
          // so the wiring stays local: no global
          // `document.querySelector`, and no cross-talk if more than
          // one overlay is ever mounted.
          const wrap = track.closest(".attachment-preview-audio-wrap") as HTMLElement | null;
          const scoped = wrap?.querySelector(
            '[data-testid="attachment-preview-audio"]',
          ) as HTMLAudioElement | null;
          if (scoped) return scoped;
          // Fallback for the very first paint, when the track has
          // not been inserted into the DOM yet and `closest` cannot
          // reach the wrapper.
          return (document.querySelector('[data-testid="attachment-preview-audio"]') ??
            null) as HTMLAudioElement | null;
        },
        (seconds: number, track: HTMLElement) => {
          const wrap = track.closest(".attachment-preview-audio-wrap") as HTMLElement | null;
          const media = (wrap?.querySelector('[data-testid="attachment-preview-audio"]') ??
            null) as HTMLAudioElement | null;
          // `applySeek` swallows the "metadata not loaded yet" error
          // so the operator can simply try again.
          if (media) {
            applySeek(media, seconds);
            requestAttachmentSeek(media.dataset.attachmentId ?? "", seconds);
          }
        },
      )}
    </div>
  `;
}

/**
 * Video body — a native `<video controls>` element. The wrapper is
 * height-capped to the standard preview max height and the
 * element uses `object-fit: contain` so a portrait phone video
 * does not letterbox off-screen.
 *
 * `playsinline` is required on iOS Safari — without it, tapping
 * the video forces it into the system fullscreen player and the
 * overlay becomes invisible. Trivial attribute but easy to miss.
 *
 * `preload="metadata"` mirrors the audio body — first frame is
 * enough to know the codec is decodable; the rest streams on
 * play.
 *
 * ## JAISIU-4437 — seekable progress bar
 *
 * The native control chrome is the operator's only scrubber, and it
 * auto-hides on idle. On top of that, the browser's own scrubber is a
 * thin sliver on the overlay's scaled backdrop and is not reachable
 * once the overlay's custom chrome takes keyboard focus. So the
 * overlay renders its OWN always-visible progress bar underneath the
 * video.
 *
 * The bar is driven by the `<video>` element's own state:
 *
 *   - `video.duration`    — total clip length.
 *   - `video.currentTime` — playhead position.
 *
 * Clicking the track seeks to `ratio * duration`; dragging seeks
 * continuously. The native `controls` stay enabled, so the existing
 * play / pause / volume / fullscreen behaviour is untouched.
 *
 * The bar's rendered state is refreshed imperatively (see
 * `updateVideoProgressBar`) from the media element's own events
 * rather than by re-rendering the overlay — re-rendering would tear
 * down and recreate the `<video>` element on every tick, which
 * restarts playback and discards the buffered range.
 */
function renderVideoBody(a: ChatAttachment): TemplateResult {
  return html`
    <div
      class="attachment-preview-video-wrap attachment-viewer-video-wrap"
      data-testid="attachment-preview-video-wrap"
      tabindex="0"
    >
      <video
        class="attachment-preview-video attachment-viewer-video"
        preload="metadata"
        playsinline
        src=${a.url ?? ""}
        title=${a.filename || "attached video"}
        style="max-width:${ATTACHMENT_PREVIEW_MAX_WIDTH_VW}vw;max-height:${ATTACHMENT_PREVIEW_MAX_HEIGHT_VH}vh;width:100%;height:auto;"
        data-testid="attachment-preview-video"
        data-attachment-id=${a.id}
        @loadedmetadata=${(e: Event) => {
          syncVideoProgress(e.currentTarget as HTMLVideoElement);
        }}
        @durationchange=${(e: Event) => {
          syncVideoProgress(e.currentTarget as HTMLVideoElement);
        }}
        @timeupdate=${(e: Event) => {
          syncVideoProgress(e.currentTarget as HTMLVideoElement);
        }}
        @seeked=${(e: Event) => {
          syncVideoProgress(e.currentTarget as HTMLVideoElement);
        }}
        @play=${(e: Event) => {
          syncVideoProgress(e.currentTarget as HTMLVideoElement);
        }}
        @pause=${(e: Event) => {
          syncVideoProgress(e.currentTarget as HTMLVideoElement);
        }}
        @ended=${(e: Event) => {
          syncVideoProgress(e.currentTarget as HTMLVideoElement);
        }}
      ></video>
      ${renderVideoProgressBar(
        (track: HTMLElement) => {
          // Resolve the media element relative to the bar that owns
          // the gesture. The bar and the `<video>` share the wrapper,
          // so the wiring stays local: no global
          // `document.querySelector`, and no cross-talk if more than
          // one overlay is ever mounted.
          //
          // JAISIU-4443 — the wrapper class on this branch is
          // `attachment-preview-video-wrap` (the older
          // `attachment-preview-media-wrap` never landed here).
          // `.attachment-preview-media-wrap` is kept in the selector
          // list so the module stays forward-compatible if the
          // wrapper is ever unified.
          const wrap = track.closest(
            ".attachment-preview-video-wrap, .attachment-preview-media-wrap",
          ) as HTMLElement | null;
          const scoped = wrap?.querySelector(
            '[data-testid="attachment-preview-video"]',
          ) as HTMLVideoElement | null;
          if (scoped) return scoped;
          // Fallback for the very first paint, when the track has
          // not been inserted into the DOM yet and `closest` cannot
          // reach the wrapper.
          return (document.querySelector(
            '[data-testid="attachment-preview-video"]',
          ) ?? null) as HTMLVideoElement | null;
        },
        (seconds: number, track: HTMLElement) => {
          const wrap = track.closest(
            ".attachment-preview-video-wrap, .attachment-preview-media-wrap",
          ) as HTMLElement | null;
          const media = (wrap?.querySelector(
            '[data-testid="attachment-preview-video"]',
          ) ?? null) as HTMLVideoElement | null;
          // `applySeek` swallows the "metadata not loaded yet" error
          // so the operator can simply try again.
          if (media) applyVideoSeek(media, seconds);
        },
      )}
    </div>
  `;
}

/**
 * Public Office Web Viewer URL for embedding an Office document.
 *
 * The viewer is a public Microsoft service that renders .xlsx,
 * .xls, .xlsm, .docx, .doc, .docm, .odt, .rtf, .pptx, .csv inside
 * an iframe given a publicly reachable `src=`. It does **not**
 * accept:
 *   - `blob:` / `data:` URLs (the viewer's own iframe cannot
 *     reach in-browser data).
 *   - Authenticated URLs (cookies are not forwarded).
 *   - Internal / private network URLs (the viewer runs on the
 *     public internet; it would 404 or refuse).
 *
 * For non-public URLs the caller falls back to the
 * download-fallback page instead of attempting an embed.
 */
export const OFFICE_WEB_VIEWER_EMBED_BASE =
  "https://view.officeapps.live.com/op/embed.aspx";



/**
 * JAISIU-4430 — spreadsheet / document bodies render the **server-extracted
 * text** locally. They deliberately do NOT embed the file in Microsoft's
 * Office Web Viewer.
 *
 * D8 (security): a session-file URL is an authenticated gateway URL. An
 * Office Viewer embed would hand that URL — and with it the operator's
 * access to the file — to `view.officeapps.live.com`, a third party, on
 * every preview. `buildOfficeViewerEmbed` is retained below ONLY as a
 * tested decision helper for a future *opt-in, public-URL-only* path; no
 * body calls it today.
 *
 * When the server extracted no text (scanned PDF-like content, a format
 * the extractor skipped, or a row persisted before extraction existed) we
 * say so plainly rather than implying the file was read, and offer the
 * download.
 */
function renderTextExtractedBody(a: ChatAttachment): TemplateResult {
  const text = (a.text ?? "").trim();
  const displayName = attachmentDisplayName(a);
  if (!text) {
    return html`
      <div
        class="attachment-preview-text-empty"
        data-testid="attachment-preview-text-empty"
        data-attachment-id=${a.id}
      >
        <p class="attachment-preview-note attachment-viewer-note">
          No text preview is available for ${displayName}. The file was not opened or analysed.
        </p>
        <a
          class="btn"
          href=${a.url ?? ""}
          download=${a.filename || "attachment"}
          rel="noopener noreferrer"
          data-testid="attachment-preview-inline-download"
        >
          Download ${displayName}
        </a>
      </div>
    `;
  }
  return html`
    <div
      class="attachment-preview-text-wrap"
      data-testid="attachment-preview-text-wrap"
      data-attachment-id=${a.id}
    >
      <pre
        class="attachment-preview-text attachment-viewer-text"
        data-testid="attachment-preview-text"
      >
${text}</pre>
    </div>
  `;
}

/** Spreadsheet body — server-extracted rows/CSV text, rendered locally. */
function renderSpreadsheetBody(
  a: ChatAttachment,
  onSelectSheet?: (index: number) => void,
): TemplateResult {
  // JAISIU-4449 — a spreadsheet with the *prepared tabular* preview
  // renders as a real table (column headers + sample rows). The
  // prepared data is authoritative: it is the same schema the agent
  // sees, so what the operator reads is what the model saw.
  const tabular = a.tabular;
  if (tabular && hasRenderableTabularContent(tabular)) {
    return renderTabularBody(a, tabular, onSelectSheet);
  }
  return renderTextExtractedBody(a);
}

/**
 * JAISIU-3533 — true when the prepared tabular payload carries anything
 * worth rendering.
 *
 * The check follows every row source the gateway may have sent: the full
 * `rows`, the legacy `sampleRows`, or the rows of any sheet in a
 * multi-sheet workbook.
 */
export function hasRenderableTabularContent(
  tabular: NonNullable<ChatAttachment["tabular"]>,
): boolean {
  if (tabular.columns.length > 0) return true;
  if ((tabular.rows?.length ?? 0) > 0) return true;
  if (tabular.sampleRows.length > 0) return true;
  return (tabular.sheets ?? []).some(
    (sheet) => sheet.columns.length > 0 || sheet.rows.length > 0,
  );
}

/**
 * JAISIU-3533 — the sheet currently on screen in a multi-sheet workbook.
 *
 * The overlay is a plain function template, not a stateful component, so
 * the active tab lives in a module-level slot. The slot is keyed by the
 * attachment id so opening a different file starts on its first sheet.
 */
const activeSheetIndexByAttachment = new Map<string, number>();

/**
 * Resolve which sheet of `tabular` should be on screen.
 *
 * Falls back to the primary sheet (index 0) whenever the remembered index
 * is out of range — which is what happens when the operator opens a
 * file whose prepared preview has not arrived yet and only later gains
 * its sheet list.
 */
export function resolveActiveSheetIndex(
  attachmentId: string,
  sheetCount: number,
): number {
  if (sheetCount <= 0) return 0;
  const remembered = activeSheetIndexByAttachment.get(attachmentId) ?? 0;
  return remembered >= 0 && remembered < sheetCount ? remembered : 0;
}

/**
 * Remember the sheet the operator selected for one attachment.
 *
 * Exported for tests; the overlay itself calls it from the tab click
 * handler.
 */
export function setActiveSheetIndex(attachmentId: string, index: number): void {
  if (!attachmentId) return;
  activeSheetIndexByAttachment.set(attachmentId, index);
}

/**
 * JAISIU-3533 — render the preamble rows that sat above the header.
 *
 * A real spreadsheet starts with a title / account / date-range block. The
 * gateway keeps those rows instead of dropping them, and the overlay shows
 * them above the table as the sheet's own context.
 */
function renderPreamble(preamble: string[][]): TemplateResult | typeof nothing {
  if (!Array.isArray(preamble) || preamble.length === 0) return nothing;
  const lines = preamble
    .map((row) => (row ?? []).map((cell) => (cell ?? "").trim()).filter(Boolean).join(" · "))
    .filter((line) => line.length > 0);
  if (lines.length === 0) return nothing;
  return html`
    <div
      class="attachment-preview-table-preamble"
      data-testid="attachment-preview-table-preamble"
    >
      ${lines.map((line) => html`<div class="attachment-preview-table-preamble-line">${line}</div>`)}
    </div>
  `;
}

/**
 * JAISIU-4449 — render the prepared tabular preview as a real table.
 *
 * The header row carries the prepared column names; the body rows are
 * the prepared rows. When the file holds more rows than the gateway
 * prepared, the footer says so honestly ("showing N of M rows") rather
 * than implying the table is complete.
 *
 * JAISIU-3533 — three changes:
 *
 *   1. **The whole table.** The gateway now ships every row, not a
 *      five-row excerpt, so the operator sees the entire sheet.
 *   2. **The preamble.** The metadata rows above the detected header are
 *      shown above the table instead of vanishing.
 *   3. **Sheet tabs.** A multi-sheet workbook gets a tab strip, the way
 *      Excel does, instead of silently showing only the first sheet.
 */
function renderTabularBody(
  a: ChatAttachment,
  tabular: NonNullable<ChatAttachment["tabular"]>,
  onSelectSheet?: (index: number) => void,
): TemplateResult {
  const displayName = attachmentDisplayName(a);
  const sheets = Array.isArray(tabular.sheets) ? tabular.sheets : [];

  // A multi-sheet workbook shows the selected sheet; a single-sheet file
  // (CSV / JSON / one-tab workbook) uses the top-level fields.
  const selectedIndex =
    sheets.length > 0 ? resolveActiveSheetIndex(a.id, sheets.length) : 0;
  const sheet = sheets.length > 0 ? sheets[selectedIndex] : undefined;

  const columns = sheet ? sheet.columns : tabular.columns;
  // The full row set wins; `sampleRows` is the fallback for an older
  // gateway that only shipped an excerpt.
  const rows = sheet
    ? sheet.rows
    : (tabular.rows ?? tabular.sampleRows);
  const rowCount = sheet ? sheet.rowCount : tabular.rowCount;
  const preamble = sheet ? sheet.preamble : (tabular.preamble ?? []);

  const shown = rows.length;
  const truncated = rowCount > shown;

  return html`
    <div
      class="attachment-preview-table-wrap"
      data-testid="attachment-preview-table-wrap"
      data-attachment-id=${a.id}
      data-column-count=${columns.length}
      data-row-count=${rowCount}
      data-sample-row-count=${shown}
      data-sheet-count=${sheets.length}
      data-active-sheet=${selectedIndex}
    >
      ${sheets.length > 1
        ? html`
            <div class="attachment-preview-sheet-tabs" data-testid="attachment-preview-sheet-tabs">
              ${sheets.map(
                (tab, index) => html`
                  <button
                    type="button"
                    class="attachment-preview-sheet-tab ${index === selectedIndex
                      ? "attachment-preview-sheet-tab--active"
                      : ""}"
                    data-testid="attachment-preview-sheet-tab"
                    data-sheet-index=${index}
                    data-sheet-name=${tab.name}
                    aria-pressed=${index === selectedIndex ? "true" : "false"}
                    @click=${() => {
                      setActiveSheetIndex(a.id, index);
                      onSelectSheet?.(index);
                    }}
                  >
                    ${tab.name || `Sheet ${index + 1}`}
                    <span class="attachment-preview-sheet-tab-count">${tab.rowCount}</span>
                  </button>
                `,
              )}
            </div>
          `
        : null}
      ${renderPreamble(preamble)}
      <table class="attachment-preview-table" data-testid="attachment-preview-table">
        ${columns.length > 0
          ? html`
              <thead>
                <tr>
                  ${columns.map((col) => html`<th scope="col">${col}</th>`)}
                </tr>
              </thead>
            `
          : null}
        <tbody>
          ${rows.map(
            (row) => html`
              <tr>
                ${row.map((cell) => html`<td>${cell}</td>`)}
              </tr>
            `,
          )}
        </tbody>
      </table>
      <p
        class="attachment-preview-note attachment-viewer-note"
        data-testid="attachment-preview-table-note"
      >
        ${truncated
          ? `Showing ${shown} of ${rowCount} rows from ${displayName}.`
          : `${shown} row${shown === 1 ? "" : "s"} from ${displayName}.`}
      </p>
    </div>
  `;
}

/**
 * JAISIU-4427 — per-kind header chrome (title + icon).
 *
 * Kept beside {@link renderBodyForKind} so the two stay in step: the
 * label the operator reads in the header always names the body they
 * are actually looking at.
 *
 * JAISIU-4435 — the icon is a type-specific Lucide glyph, so a PDF
 * shows `filePdf` and an Excel file shows `fileSpreadsheet` in the
 * overlay header instead of a generic document icon. The title strings
 * match {@link attachmentPreviewTitleFor} exactly.
 */
function headerChromeForKind(kind: AttachmentKind | SessionFileKind | string | undefined): {
  title: string;
  icon: TemplateResult;
} {
  switch (kind) {
    case "image":
      return { title: "Image preview", icon: icons.image };
    case "pdf":
      return { title: "PDF preview", icon: icons.filePdf };
    case "video":
      return { title: "Video preview", icon: icons.film };
    case "audio":
      return { title: "Audio preview", icon: icons.volume2 };
    case "spreadsheet":
    case "tabular":
      return { title: "Spreadsheet preview", icon: icons.fileSpreadsheet };
    case "document":
      return { title: "Document preview", icon: icons.fileText };
    case "code":
    case "text":
      return { title: "Code preview", icon: icons.fileCode };
    case "file":
    case "binary":
    default:
      return { title: "Attachment preview", icon: icons.archive };
  }
}
/**
 * Document body — same local text extraction as
 * {@link renderSpreadsheetBody}; kept as a separate function so the
 * header title ("Document preview" vs "Spreadsheet preview") and the
 * per-kind `data-testid`s stay distinct.
 */
function renderDocumentBody(a: ChatAttachment): TemplateResult {
  // JAISIU-4479 — a DOCX whose OOXML could be parsed arrives with
  // `preview.blocks`: the headings, lists, paragraphs and tables of the
  // document. Those render as real HTML (a heading is an `<h2>`, an
  // ordered list is an `<ol>` with its numbering, a table is a
  // `<table>`), which is what the operator actually wants to read.
  //
  // The flat extracted text is still the *fallback*: a PDF, a plain-text
  // file, a DOCX the gateway could not parse, or a file whose prep never
  // ran all land here. And when neither the blocks nor the text are
  // there, the honest "not opened or analysed" message stays on screen.
  const blocks = renderDocumentBlocks(a.blocks);
  if (blocks) {
    return blocks;
  }
  return renderTextExtractedBody(a);
}

/**
 * JAISIU-4430 — retained decision helper, currently unused by any body.
 *
 * Returns an Office Web Viewer embed URL for a publicly reachable
 * http(s) source, or `null` for anything else (`blob:`, `data:`,
 * `file:`, relative paths, empty).
 *
 * It is NOT used to preview an attachment today because doing so would
 * leak an authenticated session-file URL to a third party (D8). It is
 * kept — and kept tested — so that a future opt-in path has one tested
 * place where the "is this safe to send off-box" decision lives.
 */
export function buildOfficeViewerEmbed(src: string | undefined | null): string | null {
  if (!src) return null;
  const trimmed = src.trim();
  if (!trimmed) return null;
  if (!/^https?:\/\//i.test(trimmed)) return null;
  return `${OFFICE_WEB_VIEWER_EMBED_BASE}?src=${encodeURIComponent(trimmed)}`;
}

function renderOverlayFallback(
  props: AttachmentPreviewOverlayProps,
): TemplateResult | typeof nothing {
  if (!props.attachment) return nothing;
  const a = props.attachment;
  const displayName = attachmentDisplayName(a);
  return html`
    <div
      class="attachment-preview-overlay attachment-viewer-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="attachment-preview-title"
      data-testid="attachment-preview-fallback"
      data-preview-mode=${props.mode}
      @click=${(e: Event) => {
        if (e.target === e.currentTarget) props.onClose();
      }}
      @keydown=${(e: KeyboardEvent) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          props.onClose();
        }
      }}
    >
      <div
        class="attachment-preview-card attachment-preview-card--narrow attachment-viewer-card attachment-viewer-card--narrow"
        @click=${(e: Event) => e.stopPropagation()}
      >
        <header class="attachment-preview-header attachment-viewer-header">
          <h2
            id="attachment-preview-title"
            class="attachment-preview-title attachment-viewer-title"
          >
            Download attachment
          </h2>
          <button
            type="button"
            class="btn icon attachment-preview-close attachment-viewer-close"
            title="Close (Esc)"
            aria-label="Close"
            data-testid="attachment-preview-close"
            @click=${props.onClose}
          >
            ${icons.x}
          </button>
        </header>
        <div
          class="attachment-preview-body attachment-preview-body--fallback attachment-viewer-body attachment-viewer-body--fallback"
        >
          <p>${displayName} cannot be previewed in-app.</p>
          <a
            class="btn"
            href=${a.url}
            download=${a.filename || "attachment"}
            rel="noopener noreferrer"
            data-testid="attachment-preview-fallback-download"
          >
            Download ${displayName}
          </a>
        </div>
      </div>
    </div>
  `;
}

/**
 * Decide what to do when an attachment element is clicked.
 *
 *   - `viewer`   — open the in-app preview overlay for the
 *                  attachment. Both pre-send and post-send call
 *                  sites use this; the `mode` only changes the
 *                  overlay's download affordance, not the routing.
 *   - `download` — trigger a download (non-previewable kinds, or
 *                  the fallback when the viewer refuses to render).
 *   - `ignore`   — modifier keys are held; let the browser's
 *                  default new-tab / new-window behaviour take over.
 */
export type AttachmentPreviewAction =
  | { kind: "viewer"; attachment: ChatAttachment }
  | { kind: "download"; attachment: ChatAttachment }
  | { kind: "ignore"; reason: "modifier"; attachment: ChatAttachment };

/**
 * Pure routing for attachment clicks. Honours modifier keys exactly
 * the way the chip renderer does, so power users keep ⌘-click →
 * new-tab and middle-click → new-window without surprises.
 *
 * The `mode` argument is part of the public API surface (so the
 * call site is explicit) but the routing is identical for both
 * modes — pre-send and post-send both go to the viewer for
 * previewable kinds, both go to download otherwise.
 */
export function decideAttachmentPreviewAction(
  a: ChatAttachment,
  ev?: {
    metaKey?: boolean;
    ctrlKey?: boolean;
    shiftKey?: boolean;
    altKey?: boolean;
    button?: number;
  },
  _mode: AttachmentPreviewMode = "post-send",
): AttachmentPreviewAction {
  // Power-user affordances: any modifier or middle-click passes
  // through to the browser's default new-tab / new-window behaviour.
  if (ev && (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey || ev.button === 1)) {
    return { kind: "ignore", reason: "modifier", attachment: a };
  }
  if (attachmentIsPreviewable(a)) {
    return { kind: "viewer", attachment: a };
  }
  return { kind: "download", attachment: a };
}

/**
 * Trigger a download for an attachment. Used by the click contract
 * and by the viewer fallback. We synthesise an `<a download>` click
 * rather than navigating, so the URL never replaces the chat view.
 */
export function downloadAttachment(a: ChatAttachment): void {
  const a_ = document.createElement("a");
  a_.href = a.url ?? "";
  a_.download = a.filename || "attachment";
  a_.rel = "noopener noreferrer";
  a_.style.display = "none";
  document.body.appendChild(a_);
  a_.click();
  document.body.removeChild(a_);
}

/* ------------------------------------------------------------------ *
 * Imperative open helper (singleton root) — the integration seam that
 * lets the composer chip and the message-side image block open the
 * same Lit template without each owning Lit state. The actual
 * rendering is delegated to `renderAttachmentPreviewOverlay`; the
 * only imperative part is the mount/unmount lifecycle.
 * ------------------------------------------------------------------ */

/**
 * Options for {@link openAttachmentPreviewOverlay}. The shape mirrors
 * `AttachmentPreviewOverlayProps` but the `open` field is implied
 * (we are opening it) and the close callback is wired internally.
 */
export interface OpenAttachmentPreviewOptions {
  mode: AttachmentPreviewMode;
  onDownload?: (a: ChatAttachment) => void;
  /**
   * Optional element to focus when the overlay opens. Defaults to
   * the overlay container so the next Tab key press moves into the
   * close button.
   */
  focusOnOpen?: HTMLElement | null;
  /**
   * Optional batch of attachments the operator is paging through
   * with ← / →. When `batch.length > 1` the overlay renders
   * prev/next chevrons and binds ArrowLeft / ArrowRight to walk
   * the batch. The imperative helper tracks the active index
   * internally, so callers do not have to re-call this function
   * for each navigation step — they can pass the full batch once
   * and let the helper cycle through it. Re-calling
   * `openAttachmentPreviewOverlay` with a different
   * `attachment.id` and the same `batch` switches the active
   * member without unmounting the overlay.
   */
  batch?: ChatAttachment[] | null;
  /**
   * Initial index of `attachment` inside `batch`. Defaults to
   * finding the first element whose `id` matches `attachment.id`.
   */
  batchIndex?: number;
  /**
   * JAISIU-4449 — load the *prepared preview* for a spreadsheet or a
   * document attachment.
   *
   * The gateway's session-file prepare step already produced the
   * tabular schema (columns + sample rows) and the extracted document
   * text; the host supplies a callback that reads them through
   * `GET /api/session/files/<id>/content` and returns the enriched
   * attachment.
   *
   * The overlay paints immediately with whatever the attachment
   * already carries and re-renders once the preview arrives. When the
   * callback returns `null` (or the file has no derivable preview),
   * the honest "not analysed" message stays on screen — the fetch
   * never invents columns or an empty document body.
   */
  loadPreview?: (a: ChatAttachment) => Promise<ChatAttachment | null>;
}

/** Singleton root that holds the open overlay. */
let activeRoot: HTMLDivElement | null = null;
/** The overlay element the template renders into. */
let activeOverlay: HTMLDivElement | null = null;
/**
 * Element to restore focus to when the overlay closes. Set on open,
 * used on close so keyboard users land back where they were.
 */
let restoreFocusTo: HTMLElement | null = null;
/**
 * Active batch held by the imperative helper so ← / → navigation
 * can cycle through it without the caller re-opening the overlay.
 * Reset on every `openAttachmentPreviewOverlay` call that supplies
 * a new `batch`; preserved across re-opens that omit `batch` (the
 * common case for the message-side chip, which only needs to
 * navigate within the same message's files).
 */
let activeBatch: ChatAttachment[] | null = null;
/** Index of the currently shown attachment inside `activeBatch`. */
let activeBatchIndex: number = 0;
/**
 * JAISIU-4449 — the attachment currently rendered in the overlay.
 *
 * Used to detect a stale async preview load: when the operator
 * navigates to another attachment (or closes the overlay) while a
 * preview fetch is in flight, the resolved payload belongs to an
 * attachment that is no longer on screen and must not be painted.
 */
let activeAttachment: ChatAttachment | null = null;
/**
 * JAISIU-4447 — the id of the attachment whose media element is
 * currently mounted in the overlay.
 *
 * Tracked so the overlay can drop that attachment's pending-seek
 * state when it closes: a seek requested against the detached media
 * element can never land, and a stale pending target would keep
 * overriding the media's real position on the next preview of the
 * same file.
 */
let activePlaybackAttachmentId: string | null = null;

/**
 * Tear down the active overlay (if any) and restore focus to the
 * element that was focused before the overlay opened.
 */
function closeActiveOverlay(): void {
  if (activeRoot) {
    render(nothing, activeRoot);
    activeRoot.remove();
    activeRoot = null;
    activeOverlay = null;
  }
  // JAISIU-4449 — drop the shown attachment so a preview fetch that
  // resolves after the close cannot repaint a torn-down overlay.
  activeAttachment = null;
  // JAISIU-4447 — a seek that was requested against the now-detached
  // media element can NEVER land: the element is gone, so no `seeked`
  // event will ever arrive to settle it. Left pending, that stale
  // target keeps winning over the media's real position the next time
  // the same attachment is previewed (the bar would open at the old
  // seek target instead of the media's actual position). Clearing the
  // state on close drops the in-flight seek and leaves the settled
  // position (the last known playhead) intact for the re-open.
  if (activePlaybackAttachmentId) {
    resetAttachmentPlaybackState(activePlaybackAttachmentId);
    activePlaybackAttachmentId = null;
  }
  // We intentionally preserve `activeBatch` / `activeBatchIndex`
  // across close — a chip that closes the preview and re-opens
  // it for the same message a moment later expects the same
  // prev / next behaviour. `openAttachmentPreviewOverlay` resets
  // them when the caller supplies a fresh batch.
  if (restoreFocusTo && document.contains(restoreFocusTo)) {
    try {
      restoreFocusTo.focus();
    } catch {
      // The element may have been detached by the time we tried to
      // restore focus; that's fine — the focus just lands on body.
    }
    restoreFocusTo = null;
  }
}

/**
 * Imperatively open the unified preview overlay for the given
 * attachment. Mounts the same Lit template that
 * {@link renderAttachmentPreviewOverlay} uses; the only difference
 * is that this helper owns the mount/unmount lifecycle (so the
 * caller does not have to thread open/close state through a Lit
 * parent).
 *
 * Re-opening for a different attachment replaces the active
 * overlay; the previous overlay's close hook is not called (the
 * call sites always pass the same close semantics — "user is
 * switching to a different preview").
 *
 * If `options.batch` is supplied (or already set on a previous
 * open), the overlay renders prev/next chevrons and ArrowLeft /
 * ArrowRight cycles through the batch. The helper remembers the
 * active batch so the caller does not have to re-supply it on
 * every nav step.
 */
export function openAttachmentPreviewOverlay(
  attachment: ChatAttachment,
  options: OpenAttachmentPreviewOptions,
): void {
  if (typeof document === "undefined") {
    return; // not in a browser context (node tests)
  }
  if (!activeRoot) {
    activeRoot = document.createElement("div");
    activeRoot.className = "attachment-preview-root";
    document.body.appendChild(activeRoot);
  }
  // Remember the currently focused element so we can restore focus
  // on close. Capture BEFORE mounting so a re-open in the same
  // tick does not record the new overlay as the "previous" focus.
  const previouslyFocused = document.activeElement;
  restoreFocusTo = previouslyFocused instanceof HTMLElement ? previouslyFocused : null;

  // Refresh the active batch when the caller supplied one. When
  // the caller omits `batch` but the previous open had one, keep
  // the previous batch so a follow-up open at a different index
  // does not lose the navigation context.
  if (options.batch && options.batch.length > 0) {
    activeBatch = options.batch;
    const found = options.batch.findIndex((b) => b.id === attachment.id);
    activeBatchIndex =
      typeof options.batchIndex === "number" && options.batchIndex >= 0
        ? options.batchIndex
        : found >= 0
          ? found
          : 0;
  } else if (activeBatch) {
    // Update the index of the new attachment if it is already a
    // known batch member, otherwise leave the index alone (the
    // batch is still the same context — the caller may have
    // re-opened for the same id and we are just refreshing).
    const found = activeBatch.findIndex((b) => b.id === attachment.id);
    if (found >= 0) activeBatchIndex = found;
  } else {
    activeBatch = null;
    activeBatchIndex = 0;
  }

  const onClose = () => {
    closeActiveOverlay();
  };

  /**
   * JAISIU-4449 — the attachment currently on screen.
   *
   * Kept in a module-level slot so the async preview load can re-render
   * the overlay with the enriched attachment (the prepared tabular
   * schema or the extracted document text) without re-entering
   * {@link openAttachmentPreviewOverlay} — re-entering would reset the
   * focus-restore bookkeeping.
   */
  activeAttachment = attachment;
  // JAISIU-4447 — remember which attachment's media element is
  // mounted, so `closeActiveOverlay` can drop its pending seek.
  activePlaybackAttachmentId = attachment.id || null;

  /** Paint the overlay with the given attachment. */
  function paint(target: ChatAttachment): void {
    if (!activeRoot) return;
    activeAttachment = target;
    // JAISIU-4447 — the mounted media element changed; the pending
    // seek of the previous one can no longer land, so it is dropped.
    if (activePlaybackAttachmentId && activePlaybackAttachmentId !== target.id) {
      resetAttachmentPlaybackState(activePlaybackAttachmentId);
    }
    activePlaybackAttachmentId = target.id || null;
    const template = renderAttachmentPreviewOverlay({
      open: true,
      attachment: target,
      mode: options.mode,
      onClose,
      onDownload: options.onDownload,
      batch: activeBatch,
      batchIndex: activeBatchIndex,
      onNavigate,
      // JAISIU-3533 — a sheet-tab click repaints the table body with
      // the newly selected sheet. The selection itself is remembered in
      // the module-level slot (see `setActiveSheetIndex`), so the
      // repaint reads it back.
      onSelectSheet: () => paint(target),
      ...(options.loadPreview ? { loadPreview: options.loadPreview } : {}),
    });
    render(template, activeRoot);
  }

  /**
   * JAISIU-4449 / JAISIU-4451 — fetch the prepared preview for a
   * spreadsheet or a document that does not carry one yet.
   *
   * The overlay paints immediately with whatever the model has (often
   * nothing), so the operator never waits on a blank dialog; when the
   * prepared data arrives we re-render with it. If the gateway has no
   * preview for the file, the already-painted body keeps the honest
   * "not analysed" message — the fetch never fabricates one.
   *
   * JAISIU-4451 — this runs for the *initial* attachment **and** for
   * every attachment the operator navigates to inside the batch. The
   * prepared preview is a per-file payload, so a spreadsheet reached
   * with a single ArrowRight needs its own load; without this the
   * mixed-kind navigation silently showed "not analysed" for the
   * spreadsheet / document members.
   */
  function loadPreparedPreviewFor(target: ChatAttachment): void {
    if (!options.loadPreview) return;
    if (!attachmentNeedsPreparedPreview(target)) return;
    if (attachmentHasPreparedPreview(target)) return;
    void options
      .loadPreview(target)
      .then((loaded) => {
        // The operator may have closed the overlay or navigated to a
        // different attachment while the fetch was in flight; in
        // either case the result is stale and must not be painted.
        if (!activeRoot || !activeAttachment) return;
        if (activeAttachment.id !== target.id) return;
        const enriched = applyPreparedPreview(activeAttachment, loaded);
        if (enriched !== activeAttachment) {
          paint(enriched);
        }
      })
      .catch(() => {
        // A failed preview fetch is not an error the operator needs to
        // see: the honest "not analysed" message is already on screen.
      });
  }

  function onNavigate(next: ChatAttachment, nextIndex: number): void {
    // Cycle to the next batch member without re-mounting the
    // overlay (Lit re-renders into the same root). We do not
    // call openAttachmentPreviewOverlay here to avoid touching
    // the focus-restore state — the user is staying in the
    // overlay.
    if (!activeRoot) return;
    activeBatchIndex = nextIndex;
    // Re-bind the same `onNavigate` so the *next* ArrowRight /
    // ArrowLeft / Home / End press can also navigate. Without
    // this, the first nav re-render leaves the new template's
    // `onNavigate` undefined and a second key press silently
    // no-ops. Caught by the JAISIU-3533 heterogeneous-batch
    // browser test (image → audio → video → spreadsheet →
    // document with chained ArrowRights).
    //
    // JAISIU-4451 — the `loadPreview` callback is carried over
    // too. Without it a spreadsheet / document reached by ← / →
    // in a mixed batch would never ask the gateway for its
    // prepared preview and would sit on the honest "not
    // analysed" message even though the data is there.
    const template = renderAttachmentPreviewOverlay({
      open: true,
      attachment: next,
      mode: options.mode,
      onClose,
      onDownload: options.onDownload,
      batch: activeBatch,
      batchIndex: nextIndex,
      onNavigate,
      ...(options.loadPreview ? { loadPreview: options.loadPreview } : {}),
    });
    activeAttachment = next;
    // JAISIU-4447 — the mounted media element changed; the pending
    // seek of the previous one can no longer land, so it is dropped.
    if (activePlaybackAttachmentId && activePlaybackAttachmentId !== next.id) {
      resetAttachmentPlaybackState(activePlaybackAttachmentId);
    }
    activePlaybackAttachmentId = next.id || null;
    render(template, activeRoot);
    // JAISIU-4451 — the prepared preview (tabular schema / extracted
    // document text) is fetched per attachment, so navigating to a
    // spreadsheet or a document has to kick off its own load. The
    // initial open does this below; a navigation step used to skip
    // it entirely.
    loadPreparedPreviewFor(next);
  }

  paint(attachment);

  // JAISIU-4449 / JAISIU-4451 — kick off the prepared-preview load
  // for the initially-shown attachment (see `loadPreparedPreviewFor`).
  loadPreparedPreviewFor(attachment);

  // Lit renders the template into a comment node by default; grab
  // the resulting host element so a programmatic close (closeActiveOverlay)
  // can find it via the DOM. The actual overlay div is the first
  // child of the rendered fragment.
  activeOverlay = activeRoot.querySelector(
    ".attachment-preview-overlay, .attachment-viewer-overlay",
  ) as HTMLDivElement | null;
  // Move focus into the overlay so ESC works without an extra click.
  // The overlay itself is not focusable by default; the close button
  // gets focus on the next tick so screen readers announce it.
  queueMicrotask(() => {
    const closeBtn = activeRoot?.querySelector(
      '[data-testid="attachment-preview-close"], [data-testid="attachment-viewer-close"]',
    ) as HTMLElement | null;
    closeBtn?.focus();
  });
}

/**
 * Imperative close — used by tests and by the host shell when a
 * navigation event would leave the overlay stranded over a stale
 * view.
 */
export function closeAttachmentPreviewOverlay(): void {
  closeActiveOverlay();
  // Reset the active batch on explicit close so a re-open from
  // scratch does not inherit a stale batch context. (Implicit
  // close via overlay click does not reset — the user's mental
  // "I'm looking at this message's files" context is still
  // current; they just dismissed the preview momentarily.)
  activeBatch = null;
  activeBatchIndex = 0;
}

/* ------------------------------------------------------------------ *
 * Composer pre-send URL mapping (JAISIU-4146 / inspection §2)
 * ------------------------------------------------------------------ */

/**
 * Map a pre-upload `ChatAttachment` (the wide composer shape) onto
 * the narrow `ChatAttachment` the unified overlay expects, picking
 * the *best* full-resolution URL we know about.
 *
 * The original ticket's root cause was that the composer passed the
 * *thumbnail* (a small preview) as the overlay's `url`, so the
 * viewer scaled a 256×256 thumbnail up to 90vw × 80vh and produced
 * a pixelated image. This helper fixes that with an explicit
 * precedence:
 *
 *   1. `att.dataUrl` — the original bytes, base64-encoded,
 *      held in memory while the upload is in flight. Best choice
 *      because the bytes are already there, untouched by the
 *      gateway's preview pipeline (which may downscale).
 *   2. `att.sessionFileId` — once the upload has committed, the
 *      gateway has a stable raw URL. The caller passes
 *      `resolveSessionFileUrl` so this helper stays pure; we do
 *      not call the resolver ourselves.
 *   3. `att.previewUrl` — the gateway-generated preview. Kept as
 *      a last-resort fallback so the overlay never renders an
 *      empty body; the `onerror` swap in the image body still
 *      surfaces a real failure rather than a silent blank.
 *
 * `thumbnailUrl` precedence is the inverse — we want the chip
 * thumbnail (the gateway preview, or the original when no preview
 * is available yet) to *also* flow through. The overlay uses
 * `thumbnailUrl` for nothing today but the field is part of the
 * public type, so we set it consistently.
 *
 * Pure: no DOM, no I/O. Exported for unit tests
 * (`attachment-overlay-url-source.node.test.ts` in
 * `chat-composer-attachments.ts` and the regression suite in this
 * file).
 */
export interface MapComposerAttachmentToOverlayOptions {
  /**
   * Optional URL resolver passed in by the composer for
   * `sessionFileId`-backed attachments. Should return the raw
   * session-file URL (e.g. `/api/session/files/<id>/raw`) for
   * the given id. The helper calls this with
   * `att.sessionFileId` only when both `dataUrl` and
   * `previewUrl` are missing.
   */
  resolveSessionFileUrl?: (id: string) => string | undefined;
}

/* ------------------------------------------------------------------ *
 * Prepared preview loading (JAISIU-4449)
 * ------------------------------------------------------------------ */

/**
 * True when this attachment can carry a *prepared preview* that the
 * gateway is able to serve.
 *
 * Only two kinds qualify: a spreadsheet (prepared tabular schema +
 * sample rows) and a document (prepared extracted text). Everything
 * else — images, audio, video, PDF — has its own native body and must
 * not trigger a preview fetch.
 */
export function attachmentNeedsPreparedPreview(a: ChatAttachment): boolean {
  // Dispatch on the same value the body dispatcher uses (`a.kind`), so
  // "needs a prepared preview" and "renders a prepared preview" can
  // never disagree. Both production call sites map the gateway family
  // (`tabular`, `text`, …) to the render kind before they get here.
  return a.kind === "spreadsheet" || a.kind === "document";
}

/**
 * True when the attachment already carries everything the overlay
 * needs, so no fetch is required.
 *
 * A spreadsheet needs the prepared tabular data; a document needs
 * some extracted text. When either is missing the overlay asks the
 * host for it (and falls back to the honest "not analysed" message
 * if the host cannot supply it).
 */
export function attachmentHasPreparedPreview(a: ChatAttachment): boolean {
  const kind = a.kind;
  if (kind === "spreadsheet") {
    const tabular = a.tabular;
    return Boolean(tabular && hasRenderableTabularContent(tabular));
  }
  if (kind === "document") {
    // JAISIU-4479 — a document that carries *structured blocks* already
    // has a renderable body, so it counts as prepared even when the flat
    // text is absent: the blocks are the document's content, and asking
    // the gateway again would only return the same blocks.
    if (hasRenderableBlocks(a.blocks)) { return true; }
    return Boolean((a.text ?? "").trim().length > 0);
  }
  return true;
}

/**
 * Apply a loaded preview payload onto the attachment model.
 *
 * Pure — exported for tests so the "tabular payload populates
 * `tabular`", "text payload populates `text`" and "an unavailable
 * result leaves the model untouched" contracts can be asserted
 * without a network.
 */
export function applyPreparedPreview(
  a: ChatAttachment,
  preview: {
    tabular?: NonNullable<ChatAttachment["tabular"]>;
    text?: string;
    blocks?: NonNullable<ChatAttachment["blocks"]>;
  } | null,
): ChatAttachment {
  if (!preview) return a;
  if (preview.tabular) {
    return { ...a, tabular: preview.tabular };
  }
  // JAISIU-4479 — the structured blocks ride along with the flat
  // text. They are applied together with the text, never instead of
  // it: a payload that carries blocks but no text has no document
  // *body* to show, so it is ignored and the honest "not analysed"
  // message stays on screen.
  if (typeof preview.text === "string" && preview.text.trim().length > 0) {
    const blocks =
      Array.isArray(preview.blocks) && preview.blocks.length > 0 ? preview.blocks : undefined;
    return { ...a, text: preview.text, ...(blocks ? { blocks } : {}) };
  }
  return a;
}

/**
 * JAISIU-4435 — the render kind the overlay body dispatcher should use.
 *
 * ## The bug this fixes
 *
 * This used to be `att.kind ?? attachmentKindForMime(...)`. A stored
 * composer chip carries the **gateway** kind, where a PDF is `document`.
 * Because `att.kind` was present it won, so a PDF was dispatched to
 * {@link renderTextExtractedBody} — a `<pre>` block that (for a composer
 * chip) always read "this file was not opened or analysed", because a
 * draft chip carries no extracted text. The operator clicked a PDF and
 * got an empty text box instead of the PDF.
 *
 * ## The rule
 *
 * The MIME is the finer signal, so it wins whenever it is present and
 * recognised. Only an absent or unrecognised MIME falls back to the
 * gateway kind, mapped through the render vocabulary.
 */
function overlayRenderKind(att: ChatAttachment): AttachmentKind {
  const mime = (att.mimeType ?? att.mime ?? "").trim();
  if (mime) {
    const fromMime = attachmentKindForMime(mime);
    if (fromMime !== "file") return fromMime;
  }
  const persisted = (att.kind ?? "").toLowerCase();
  switch (persisted) {
    case "image":
    case "video":
    case "audio":
    case "pdf":
    case "code":
    case "spreadsheet":
    case "document":
      return persisted;
    case "tabular":
      return "spreadsheet";
    case "text":
      return "code";
    default:
      return "file";
  }
}

export function mapComposerAttachmentToOverlay(
  att: ChatAttachment,
  opts: MapComposerAttachmentToOverlayOptions = {},
): ChatAttachment {
  const kind = overlayRenderKind(att);
  const filename = att.filename ?? att.name ?? "file";
  const sizeBytes = att.sizeBytes ?? att.size ?? 0;
  const messageId = att.messageId ?? "composer";

  // URL precedence: dataUrl > sessionFileId(raw) > previewUrl.
  let url: string | undefined;
  if (typeof att.dataUrl === "string" && att.dataUrl.length > 0) {
    url = att.dataUrl;
  } else if (att.sessionFileId && opts.resolveSessionFileUrl) {
    url = opts.resolveSessionFileUrl(att.sessionFileId);
  }
  if (!url) {
    url = att.previewUrl ?? att.url;
  }

  // thumbnailUrl precedence: previewUrl > dataUrl > url.
  const thumbnailUrl = att.previewUrl ?? att.dataUrl ?? att.thumbnailUrl ?? url;

  return {
    ...att,
    id: att.id,
    messageId,
    kind,
    mime: att.mime ?? att.mimeType ?? "",
    filename,
    sizeBytes,
    url,
    thumbnailUrl,
  };
}
